import { useMemo } from 'react'
import { useShiftEditor } from '../useShiftEditor'
import { useCompanyPosition } from 'hooks/useCompanyPosition'
import { Box, Flex, Heading, Text } from 'ui'
import { groupBy } from 'ramda'
import { BadgeCheck, CircleCheck, Shirt } from 'lucide-react'
import styled from 'styled-components'
import { PreviewCard, previewTokens } from './styles'

const RequirementWarning = styled(Text)`
  margin: 0;
  font-size: 13px;
  line-height: 18px;
`

const RequirementGroup = styled(Box)`
  margin-top: ${previewTokens.spacing.gap};
`

const CategoryRow = styled(Flex)`
  min-width: 0;
  align-items: center;
  gap: 8px;

  svg {
    flex: 0 0 auto;
  }
`

const CategoryLabel = styled(Text)`
  min-width: 0;
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  overflow-wrap: anywhere;
`

const RequirementList = styled(Box)`
  margin: 6px 0 0;
  padding-left: 22px;
  list-style: disc outside;
`

const RequirementItem = styled(Box)`
  margin: 0 0 3px;
  font-size: 13px;
  line-height: 18px;
  overflow-wrap: anywhere;

  &:last-child {
    margin-bottom: 0;
  }
`

const getCategoryIcon = (category: string) => {
  const normalizedCategory = category.toLowerCase()

  if (normalizedCategory.includes('attire')) {
    return <Shirt size={16} aria-hidden="true" />
  }

  if (normalizedCategory.includes('general')) {
    return <BadgeCheck size={16} aria-hidden="true" />
  }

  return <CircleCheck size={16} aria-hidden="true" />
}

export function RequirementSection() {
  const { shiftData } = useShiftEditor()
  const position = useCompanyPosition(shiftData?.details?.positionId)
  const requirementGroups = useMemo(() => {
    const requirements = position?.requirements2 ?? []
    const groupedRequirements = groupBy(
      (requirement) => requirement.category ?? 'Other',
      requirements
    )
    const categories = Array.from(
      new Set(
        requirements.map((requirement) => requirement.category ?? 'Other')
      )
    )

    return categories.map((category) => ({
      category,
      requirements: groupedRequirements[category] ?? [],
    }))
  }, [position])

  if (requirementGroups.length === 0) return null

  return (
    <PreviewCard>
      <Heading level={4} my={0} mb={2}>
        Requirements
      </Heading>
      <RequirementWarning>
        You may be turned away from the shift without pay if you fail to meet
        all of the requirements.
      </RequirementWarning>
      {requirementGroups.map(({ category, requirements }) => (
        <RequirementGroup key={category}>
          <CategoryRow>
            {getCategoryIcon(category)}
            <CategoryLabel>{category}</CategoryLabel>
          </CategoryRow>
          <RequirementList as={'ul'}>
            {requirements.map((requirement) => (
              <RequirementItem as={'li'} key={requirement.id}>
                {requirement.name}
              </RequirementItem>
            ))}
          </RequirementList>
        </RequirementGroup>
      ))}
    </PreviewCard>
  )
}
